import { Box, Button, Flex, Loader, Text } from '@mantine/core';
import { Navigate, NavLink, useSearchParams } from 'react-router';
import { IconArrowLeft, IconCheck } from '@tabler/icons-react';
import { useEffect } from 'react';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import { meetingDetailPath, meetingScopeFromParams } from 'src/meetings/scope';
import { useContentMeta } from 'src/UI/app-container';
import { ChatMode } from 'src/UI/app-container/chat/types';
import { GenericErrorPage } from '../../error/GenericErrorPage';
import PageLoader from '../../UI/loader/PageLoader';
import { useFileUploadWithEntities } from '../hooks/file-uploads/useFileUploadWithEntities';
import { useFamilyReviewId } from '../hooks/file-uploads/useFamilyReviewId';
import {
  selectActionedDetectionCount,
  selectCanClearFileData,
  selectFinalizeStats,
  selectIsExpired,
  selectTotalDetectionCount,
  selectVisibleCategories,
  useDetectedEntitiesStore,
} from '../stores/useDetectedEntitiesStore';
import CategoryEntities from './CategoryEntities';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { useDisclosure } from '@mantine/hooks';
import { FinalizeReviewModal } from './FinalizeReviewModal';
import { Mixpanel } from 'src/tracking/mixpanel/Mixpanel';
import { MixpanelEvents } from 'src/tracking/mixpanel/MixpanelEvents';

export default function DetectedEntitiesPage() {
  const { clientId } = useCurrentClient();
  useContentMeta({ pageType: 'client', entityId: clientId, chatMode: ChatMode.CLIENT });
  const [searchParams] = useSearchParams();
  const familyId = useFamilyReviewId();
  const params = {
    fileId: searchParams.get('fileId') ?? undefined,
    fileName: searchParams.get('fileName') ?? undefined,
    guid: searchParams.get('guid') ?? undefined,
    originMeetingId: searchParams.get('originMeetingId') ?? undefined,
    originClientId: searchParams.get('originClientId') ?? undefined,
    originFamilyId: searchParams.get('originFamilyId') ?? undefined,
  };
  const { finaliseReview, file, isLoading, isProcessing, error, refetch } =
    useFileUploadWithEntities();
  const actionedDetectionCount = useDetectedEntitiesStore(selectActionedDetectionCount);
  const totalDetectionCount = useDetectedEntitiesStore(selectTotalDetectionCount);
  const isExpired = useDetectedEntitiesStore(selectIsExpired);
  const canClear = useDetectedEntitiesStore(state => selectCanClearFileData(state, params?.guid));
  const clearFileData = useDetectedEntitiesStore(state => state.clearFileData);
  const clearExpiredFiles = useDetectedEntitiesStore(state => state.clearExpiredFiles);
  const setFileId = useDetectedEntitiesStore(state => state.setFileId);
  const visibleCategories = useDetectedEntitiesStore(selectVisibleCategories);
  const { showDataUploadCounts } = useFlags();
  const [finalizeModalVisible, { open: openFinalizeModal, close: closeFinalizeModal }] =
    useDisclosure(false);
  const counts = useDetectedEntitiesStore(selectFinalizeStats);
  const { originMeetingId, originClientId, originFamilyId } = params || {};
  useEffect(() => {
    if (canClear && params?.guid && params.fileId) {
      clearFileData(params.fileId, params.guid);
      if (!isLoading) refetch();
    }
  }, [isLoading, canClear]);

  useEffect(() => {
    if (params?.fileId) {
      setFileId(params?.fileId);
      clearExpiredFiles();
    }
  }, [params?.fileId]);

  async function handleResolveFile() {
    const total = counts.inserts + counts.updates + counts.unreviewed;
    Mixpanel.track(MixpanelEvents.DATA_UPLOAD_FILE_PROCESS_COMPLETE, {
      dataUploadFileId: params?.fileId,
      inserts: counts.inserts,
      updates: counts.updates,
      unreviewed: counts.unreviewed,
      percentageActioned: ((counts.inserts + counts.updates) / total) * 100,
      percentageIgnored: (counts.unreviewed / total) * 100,
    });
    return finaliseReview();
  }

  const backUrl = getBackUrl({
    familyId,
    originMeetingId,
    originClientId,
    originFamilyId,
    clientId,
  });

  if (!params?.fileId || (isExpired && !canClear)) {
    return <Navigate to={backUrl} />;
  }

  if (file?.isResolved && !finalizeModalVisible) {
    return <Navigate to={backUrl} />;
  }

  if (error) {
    return <GenericErrorPage error={error} />;
  }

  if (isLoading) {
    return <PageLoader absolute={false} />;
  }

  // Detection writes its rows only once the background process finishes. Showing the review
  // before then offers an empty list and a working "Finalise data", which would resolve the file
  // and close the client's review without anything having been looked at.
  if (isProcessing) {
    return (
      <Box m="lg">
        <BackToOverview to={backUrl} />
        <Flex direction="column" align="center" gap="sm" mt="xxl">
          <Loader />
          <Text c="gray.10" fw={600} size="xxl">
            Still reading this file
          </Text>
          <Text c="gray.6" size="sm">
            The data to review will appear here as soon as it&apos;s ready.
          </Text>
        </Flex>
      </Box>
    );
  }

  return (
    <Box m="lg">
      <Flex direction="row" justify="space-between" align="center">
        <BackToOverview to={backUrl} />

        <Button variant="primary" onClick={openFinalizeModal} leftSection={<IconCheck size={16} />}>
          Finalise data
        </Button>
      </Flex>

      <Text c="gray.10" fw={600} size={'xxl'} mt="xl">
        Review imported data{' '}
        {showDataUploadCounts && `(${actionedDetectionCount}/${totalDetectionCount} Done)`}
      </Text>
      <Text c="gray.6" size="sm" mt="xxs">
        Act on the items you want to keep. Anything left unreviewed will be ignored when you
        finalise.
      </Text>

      <Flex direction="column">
        {visibleCategories.map((category, i) => (
          <CategoryEntities
            key={category}
            category={category}
            bottomBorder={i !== visibleCategories.length - 1}
          />
        ))}
      </Flex>

      <FinalizeReviewModal
        opened={finalizeModalVisible}
        close={closeFinalizeModal}
        newCount={counts.inserts}
        existingCount={counts.updates}
        unreviewedCount={counts.unreviewed}
        onConfirm={handleResolveFile}
        navigateTo={backUrl}
      />
    </Box>
  );
}

function BackToOverview({ to }: { to: string }) {
  return (
    <NavLink
      to={to}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--mantine-spacing-xxs)',
        textDecoration: 'none',
      }}
      data-testid="back-button"
    >
      <IconArrowLeft style={{ cursor: 'pointer' }} color="var(--mantine-color-gray-10)" />
      <Text c="gray.10" fw={500} size="md">
        Back to data overview
      </Text>
    </NavLink>
  );
}

function getBackUrl({
  familyId,
  originMeetingId,
  originClientId,
  originFamilyId,
  clientId,
}: {
  familyId?: string;
  originMeetingId?: string;
  originClientId?: string;
  originFamilyId?: string;
  clientId?: string;
}) {
  // A review opened from a meeting returns to that meeting on the route it was opened from,
  // even for a family recording that also carries a familyId.
  if (originMeetingId) {
    const scope = meetingScopeFromParams({ familyId: originFamilyId, clientId: originClientId });
    return meetingDetailPath(scope, originMeetingId);
  }
  if (originClientId) {
    return `/client/${originClientId}/document/add`;
  }
  if (familyId) {
    return `/family/${familyId}/documents/add`;
  }
  return `/client/${clientId}/document/add`;
}
